// Anniversary card (W36.0, spec §3.1 + §2.8) — two states, one card.
//
// §3.2: 當天有 → 慶祝態（帶儀式）; 否則 → 下一個紀念日 + 倒數天數. The ritual
// treatment lives in `AnniversaryRitual`; this file only decides which state to
// render, and it is the file where the empty state lives.
//
// §2.8's 「減少使用壓力」 is why the countdown state is quiet: no seal, no
// gold, no animation. A countdown that looks like a celebration trains people
// to feel bad on ordinary days. W47.4 added a ring, which is the nearest thing
// to a seal this card now carries — `ADR-0138` records that the user chose it
// over the recommended centring, and records the tension rather than pretending
// §2.8 was satisfied.

import { Link } from "react-router-dom";

import { Card } from "@/components/ui/card";
import type { DashboardAnniversary } from "@/lib/api/dashboard";
import { DASHBOARD_I18N } from "@/lib/i18n/dashboard";
import { AnniversaryRitual } from "./AnniversaryRitual";
import { cycleProgress } from "./dashboardHelpers";

export interface DailyAnniversaryCardProps {
  anniversary: DashboardAnniversary;
  /**
   * Days together, rendered in gold during the ritual state.
   *
   * Spec §2.8 requires 「天數轉金色」 when today is an anniversary, and the
   * mockup puts that number in the *ceremony* card rather than in the hero —
   * because that is what is actually being celebrated. Passing it here is how
   * the ritual owns its own number instead of reaching back into the hero.
   */
  daysTogether?: number;
  /**
   * Drop this card's own surface so the `AccentCard` hosting it is the surface.
   *
   * W43 `ADR-0109`. With both painting, the saturated fill showed only as a 16px
   * frame around a white card — a "double card", which is the same density
   * mistake the reversal removed, with the colour on the wrong side of the inner
   * edge. Applied by the hosted card rather than forced from outside, because
   * `!bg-transparent`-style overrides are the specificity fight this codebase
   * keeps losing.
   */
  bare?: boolean;
}

/** Ring geometry. 39px radius in a 92px box leaves room for a 7px stroke. */
const RING_R = 39;
const RING_C = 2 * Math.PI * RING_R;

function CycleRing({ progress, label }: { progress: number; label: string }) {
  const pct = Math.round(progress * 100);
  return (
    <div
      className="anniv-ring"
      role="img"
      aria-label={label}
      data-testid="anniversary-cycle-ring"
      data-progress={pct}
    >
      <svg viewBox="0 0 92 92" aria-hidden="true" focusable="false">
        <circle className="anniv-ring-track" cx="46" cy="46" r={RING_R} fill="none" strokeWidth="7" />
        <circle
          className="anniv-ring-arc"
          cx="46"
          cy="46"
          r={RING_R}
          fill="none"
          strokeWidth="7"
          strokeDasharray={RING_C}
          strokeDashoffset={RING_C * (1 - progress)}
        />
      </svg>
      {/* The percentage is **visible text**, not something only the ring's shape
          says. A progress indicator whose value exists solely in a stroke
          offset is unreadable to anyone who cannot see the arc. */}
      <span className="anniv-ring-pct">{pct}%</span>
    </div>
  );
}

export function DailyAnniversaryCard({
  anniversary,
  daysTogether,
  bare = false,
}: DailyAnniversaryCardProps) {
  // --- Celebration state -------------------------------------------------
  const first = anniversary.is_today ? anniversary.today[0] : undefined;
  if (first) {
    const extra = anniversary.today.length - 1;
    return (
      <AnniversaryRitual
        bare={bare}
        data-testid="anniversary-card"
        srLabel={`今天是 ${first.title}${extra > 0 ? `，還有 ${extra} 個紀念日` : ""}${typeof daysTogether === "number" ? `，在一起 ${daysTogether} 天` : ""}`}
      >
        <p className="text-meta text-ink-3">{DASHBOARD_I18N.anniversary.today}</p>
        <h2 className="text-title-3 font-display text-ink-1">{first.title}</h2>
        {typeof daysTogether === "number" ? (
          // DESIGN §3.3 — pure numeric scenario. The gold is the token, not a
          // hex (§2.8 「非另挑」).
          <p
            data-testid="anniversary-ritual-days"
            className="mt-s-2 text-display-2 font-semibold tabular-nums text-gold"
          >
            {daysTogether}
            <span className="ml-s-1 text-body-sm font-semibold text-gold-ink">
              {DASHBOARD_I18N.hero.daysUnit}
            </span>
          </p>
        ) : null}
        {extra > 0 ? (
          <p className="mt-s-1 text-meta text-ink-3" data-testid="anniversary-extra">
            還有 {extra} 個紀念日
          </p>
        ) : null}
        <Link
          to={`/anniversaries/${first.id}`}
          data-testid="anniversary-link"
          className="press mt-s-3 inline-flex min-h-11 w-full items-center text-body-sm font-semibold text-you-strong"
        >
          看看這個紀念日
        </Link>
      </AnniversaryRitual>
    );
  }

  // --- Countdown state ---------------------------------------------------
  // W47.0, user-chosen shape **B**: the number owns the card. W47.4 keeps that
  // and adds the ring to the right (`ADR-0138`).
  //
  // The shipped shape put 「288」 in a right-hand column beside two lines of meta,
  // so the largest number on the screen had nothing holding it up. That one drops
  // the column: title and date stack, then the numeral runs full width.
  //
  // **W47.4 fixed the two things shape B left behind.** It printed 「288」 twice —
  // once as the numeral, once inside `inDays`（「還有 {count} 天」) — so the card
  // said the same thing in two sizes. And with the column gone, the numeral sat
  // left-aligned in a full-width card and the right half was empty.
  //
  // **What this costs, stated plainly.** The module docstring above records
  // §2.8's 「減少使用壓力」: 「倒數態是安靜的：no seal, no gold, no animation」 and
  // 「A countdown that looks like a celebration trains people to feel bad on
  // ordinary days」. A 76px numeral is not a seal or gold; a ring is closer to one.
  // The user was shown three shapes and picked the ring over the recommended
  // centring; `ADR-0138` records the tension rather than pretending the rule was
  // satisfied.
  const next = anniversary.next;
  if (!next) {
    // §2.9 R4.4 — never a grey empty box. Say what to do instead.
    return (
      <Card data-testid="anniversary-card" bare={bare}>
        <div className="p-s-5 flex flex-col gap-s-3" data-testid="anniversary-empty">
          <h2 className="text-title-3 font-display text-ink-1">還沒有紀念日</h2>
          <p className="text-body-sm text-ink-2">
            把重要的日子記下來, 我們就不會忘記。
          </p>
          <Link
            to="/anniversaries/new"
            data-testid="anniversary-add"
            className="press inline-flex min-h-11 w-full items-center text-body-sm font-semibold text-you-strong"
          >
            新增第一個紀念日
          </Link>
        </div>
      </Card>
    );
  }

  // W47.4 — the numeral appears **once**. `inDays` carries the number and is
  // therefore wrong here; `inDaysUnit` is the unit alone. Gate G15 exists because
  // reaching for `inDays` in this state is the single most natural way to
  // reintroduce the duplicate, and it was the original defect.
  const progress = cycleProgress(next.date, next.days_until);
  const unit =
    next.days_until === 0 ? DASHBOARD_I18N.anniversary.today : DASHBOARD_I18N.anniversary.inDaysUnit;
  const ringLabel = [
    DASHBOARD_I18N.anniversary.cycleProgress.replace("{pct}", String(Math.round((progress ?? 0) * 100))),
    // The date is no longer printed (the approved C shape), so the ring's name
    // is the only place it survives for anyone not looking at the picture.
    next.date,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Card data-testid="anniversary-card" bare={bare}>
      <div className="p-s-5" data-testid="anniversary-countdown">
        <p className="text-meta font-semibold text-ink-3">{DASHBOARD_I18N.anniversary.countdown}</p>
        <h2 className="mt-s-1 truncate text-title-3 font-display text-ink-1">{next.title}</h2>
        {/* W47.4 — the date comes back. The approved C shape had no date line and
            that was not discussed with the user, only the ring was; dropping a
            piece of information because it happened not to fit the composition is
            not a decision the mockup was ever put to the user on. Restored in
            exactly the form W47.0 shipped it, so this is a restoration rather
            than a new format. */}
        {next.date ? (
          <p className="mt-s-1 text-meta tabular-nums text-ink-3" data-testid="anniversary-date">
            {next.date}
          </p>
        ) : null}
        <div className="mt-s-3 flex items-center justify-between gap-s-4">
          <p
            data-testid="anniversary-countdown-days"
            className="flex items-baseline gap-s-2 text-display-2 font-extrabold tabular-nums tracking-tight text-ink-1"
          >
            {next.days_until}
            <span className="text-body-sm font-bold text-ink-3">{unit}</span>
          </p>
          {progress === null ? null : <CycleRing progress={progress} label={ringLabel} />}
        </div>
      </div>
    </Card>
  );
}
